// src/components/layout/Layout.jsx

import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Sidebar from "../components/sidebar/Sidebar";
import AdminQuickSearch from "../components/header/AdminQuickSearch";
import Header from "../components/header/Header";
import PageContainer from "../components/common/PageContainer";
import { PageHeaderVisibilityProvider } from "../components/common/PageHeaderVisibilityContext";
import RoutePageToolbar from "../components/common/RoutePageToolbar";
import { ADMIN_LAYOUT, getAdminShellBackground } from "../adminThemeTokens";
import useMenuData from "../hooks/useMenuData";
import { AdminNavigationProvider } from "../components/navigation/AdminNavigationContext";

const Layout = ({ schoolName, schoolId }) => {
  const theme = useTheme();
  const isDesktopHeader = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerElevated, setHeaderElevated] = useState(false);
  const resolvedSchoolId = schoolId || localStorage.getItem("schoolId") || "";
  const { menuData, loading: menuLoading, error: menuError } = useMenuData(resolvedSchoolId);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleShellScroll = (event) => {
    const nextElevated = event.currentTarget.scrollTop > 8;
    setHeaderElevated((previous) => (previous === nextElevated ? previous : nextElevated));
  };

  return (
    <AdminNavigationProvider
      menuData={menuData}
      menuLoading={menuLoading}
      menuError={menuError}
      pathname={location.pathname}
    >
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          height: "100vh",
          overflow: "hidden",
          p: 0,
          background: getAdminShellBackground(theme),
        }}
      >
        <Box
          className="admin-shell-frame"
          sx={{
            width: "100%",
            display: "flex",
            borderRadius: 0,
            border: "none",
            backgroundColor: theme.palette.background.default,
            overflow: "hidden",
          }}
        >
          <Sidebar
            mobileOpen={mobileOpen}
            handleDrawerToggle={handleDrawerToggle}
            schoolName={schoolName}
            schoolId={resolvedSchoolId}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              pl: 0,
              pr: { xs: 0.65, sm: 0.9, md: 1.2 },
              py: { xs: 0.7, sm: 0.9, md: 1.15 },
            }}
          >
            <Box
              sx={{
                position: isDesktopHeader ? "sticky" : "static",
                top: 0,
                zIndex: (themeValue) => themeValue.zIndex.appBar + 8,
                flexShrink: 0,
                pl: 0,
                pr: ADMIN_LAYOUT.shellPaddingX,
                pb: isDesktopHeader ? { md: 0.95, lg: 1.05 } : { xs: 0.55, sm: 0.7 },
              }}
            >
              {isDesktopHeader ? (
                <Header
                  handleDrawerToggle={handleDrawerToggle}
                  schoolId={resolvedSchoolId}
                  schoolName={schoolName}
                  elevated={headerElevated}
                />
              ) : (
                <AdminQuickSearch handleDrawerToggle={handleDrawerToggle} />
              )}
            </Box>
            <Box
            className="admin-page-shell"
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              px: ADMIN_LAYOUT.shellPaddingX,
              pt: 0,
              pb: ADMIN_LAYOUT.shellPaddingBottom,
              mt: 0,
              borderRadius: 0,
              border: "none",
              backgroundColor: theme.palette.background.default,
              "& > *": {
                width: "100%",
                mt: "0 !important",
                mb: "0 !important",
              },
              "& > .MuiContainer-root": {
                px: "0 !important",
              },
              "& > * > .MuiContainer-root": {
                px: "0 !important",
              },
              "& .MuiContainer-root": {
                maxWidth: "100% !important",
                mx: "0 !important",
              },
              "&::-webkit-scrollbar": {
                width: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.24),
                borderRadius: 10,
              },
            }}
            onScroll={handleShellScroll}
          >
            <Box
              className="admin-route-frame"
              sx={{
                width: "100%",
                maxWidth: "100%",
                borderRadius: 0,
                p: ADMIN_LAYOUT.pagePadding,
                backgroundColor: 'transparent',
                border: "none",
                "& > *": {
                  width: "100%",
                  maxWidth: "100% !important",
                  mx: "0 !important",
                  mt: "0 !important",
                  mb: "0 !important",
                  p: "0 !important",
                },
              "& > :is(.MuiBox-root, .MuiContainer-root)": {
                maxWidth: "100% !important",
                mx: "0 !important",
                m: "0 !important",
                p: "0 !important",
              },
              "& > * > :is(.MuiBox-root, .MuiContainer-root):first-of-type": {
                maxWidth: "100% !important",
                mx: "0 !important",
                m: "0 !important",
                p: "0 !important",
              },
              "& > * > * > :is(.MuiBox-root, .MuiContainer-root):first-of-type": {
                maxWidth: "100% !important",
                mx: "0 !important",
                m: "0 !important",
                p: "0 !important",
              },
              "& > .MuiContainer-root": {
                px: "0 !important",
              },
              "& > * > .MuiContainer-root:first-of-type": {
                px: "0 !important",
              },
              "& > * > * > .MuiContainer-root:first-of-type": {
                px: "0 !important",
              },
              "& .admin-page-content": {
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.3, sm: 1.6, md: 2 },
              },
              "& .admin-page-content .MuiTabs-root": {
                backgroundColor: "transparent",
                border: "none",
                borderRadius: 0,
              },
              "& .admin-page-content .MuiTabs-indicator": {
                height: 3,
                borderRadius: 99,
              },
              "& .admin-page-content .MuiButton-startIcon .MuiSvgIcon-root, & .admin-page-content .MuiButton-endIcon .MuiSvgIcon-root": {
                fontSize: "1rem",
              },
              "& .admin-route-viewport": {
                width: "100%",
                animation: "admin-route-fade 220ms ease-out",
              },
              "@keyframes admin-route-fade": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(8px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
              }}
            >
              <PageHeaderVisibilityProvider>
                <PageContainer>
                  <RoutePageToolbar />
                  <Box
                    className="admin-route-viewport"
                  >
                    <Outlet />
                  </Box>
                </PageContainer>
              </PageHeaderVisibilityProvider>
            </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </AdminNavigationProvider>
  );
};

export default Layout;
